/**
 * Small announcement carousel for the cart drawer: "Noté X/5 (Y avis)" shown
 * when available, "Garantie anti-casse" always reachable via the chevrons or
 * the 6s auto-rotation — same behavior as the site-wide banner (gm-annonce in
 * layout/theme.liquid), reimplemented with its own ids/classes so it can live
 * inside the cart drawer without clashing with the header's instance.
 *
 * The reviews slide's text is not computed here: it mirrors whatever the
 * header banner's #gm-annonce-avis already resolved (via the reviews app).
 *
 * 27/09/2026 fix: the cart drawer is re-rendered (morphed) after every cart
 * update, which reset the reviews slide to its server-rendered placeholder
 * ("Chargement des avis…") while this element stayed initialised, so the text
 * was never re-applied. The slide is now server-rendered empty, only takes part
 * in the rotation once it has real text, and is re-synced through
 * MutationObservers whenever the drawer markup or the header banner changes.
 */
class CartAnnounce extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized) return;
    this.dataset.initialized = 'true';

    let index = 0;
    let timer = null;
    let sourceObserver = null;
    let observedSource = null;

    const allSlides = () => Array.from(this.querySelectorAll('.cart-announce__slide'));

    // A slide takes part in the rotation unless it is the reviews slide
    // and has no text yet.
    const eligibleSlides = () =>
      allSlides().filter((slide) => slide.dataset.slide !== 'reviews' || slide.dataset.ready === 'true');

    const render = () => {
      const eligible = eligibleSlides();
      if (!eligible.length) return;
      if (index >= eligible.length) index = 0;
      allSlides().forEach((slide) => slide.classList.toggle('is-active', slide === eligible[index]));
    };

    const goTo = (nextIndex) => {
      const count = eligibleSlides().length;
      if (!count) return;
      index = (nextIndex + count) % count;
      render();
    };

    const restart = () => {
      clearInterval(timer);
      timer = setInterval(() => goTo(index + 1), 6000);
    };

    const syncReviews = () => {
      const slide = this.querySelector('[data-slide="reviews"]');
      if (!slide) return;
      const source = document.getElementById('gm-annonce-avis');
      const text = source && !source.hidden ? source.textContent.trim() : '';

      if (text) {
        if (slide.textContent.trim() !== text) slide.textContent = text;
        if (slide.dataset.ready !== 'true') slide.dataset.ready = 'true';
      } else if (slide.dataset.ready === 'true') {
        delete slide.dataset.ready;
        slide.textContent = '';
      }
      render();
    };

    const watchSource = () => {
      const source = document.getElementById('gm-annonce-avis');
      if (!source || source === observedSource) return;
      sourceObserver?.disconnect();
      observedSource = source;
      sourceObserver = new MutationObserver(syncReviews);
      sourceObserver.observe(source, {
        childList: true,
        characterData: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['hidden'],
      });
    };

    const prevButton = this.querySelector('.cart-announce__nav--prev');
    const nextButton = this.querySelector('.cart-announce__nav--next');

    prevButton?.addEventListener('click', () => {
      goTo(index - 1);
      restart();
    });
    nextButton?.addEventListener('click', () => {
      goTo(index + 1);
      restart();
    });

    this.addEventListener('mouseenter', () => clearInterval(timer));
    this.addEventListener('mouseleave', restart);

    render();
    restart();

    // Re-sync when the drawer markup is morphed back to its server-rendered state.
    new MutationObserver(syncReviews).observe(this, {
      childList: true,
      characterData: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-ready'],
    });

    // The header banner fills its reviews slide once the reviews app has loaded
    // (which can take a while) — keep looking for it, then observe it.
    syncReviews();
    let tries = 0;
    const poll = setInterval(() => {
      tries++;
      watchSource();
      syncReviews();
      if ((observedSource && tries > 3) || tries >= 100) clearInterval(poll);
    }, 500);
  }
}

if (!customElements.get('cart-announce')) {
  customElements.define('cart-announce', CartAnnounce);
}
